import { db, setCachedProfile } from '@/lib/db';
import { supabase } from '@/lib/supabase';
import { signal } from '@preact/signals';
import { reloadFromCache } from '@/state/data';

/** Delta pull: Supabase -> Dexie. Watermarked on updated_at (mutable tables)
 *  or created_at (append-only). feed_logs pulls a rolling window so undo
 *  deletes resolve without tombstones. */

export const lastSyncAt = signal<Date | null>(null);
export const syncing = signal(false);

const LOG_WINDOW_DAYS = 7;

async function watermark(key: string): Promise<string> {
  const kv = await db.kv.get(`wm.${key}`);
  return (kv?.value as string) ?? '1970-01-01T00:00:00Z';
}
async function setWatermark(key: string, iso: string) {
  await db.kv.put({ key: `wm.${key}`, value: iso });
}

async function pullTable(
  table: 'animals' | 'schedules' | 'documents',
  tsCol: 'updated_at'
): Promise<void>;
async function pullTable(
  table: 'photos' | 'reports' | 'messages' | 'timers',
  tsCol: 'created_at'
): Promise<void>;
async function pullTable(table: string, tsCol: string): Promise<void> {
  const wm = await watermark(table);
  const { data, error } = await supabase
    .from(table)
    .select('*')
    .gt(tsCol, wm)
    .order(tsCol, { ascending: true })
    .limit(1000);
  if (error || !data?.length) return;
  await (db as never as Record<string, { bulkPut: (rows: unknown[]) => Promise<unknown> }>)[
    table
  ].bulkPut(data);
  await setWatermark(table, (data[data.length - 1] as Record<string, string>)[tsCol]);
  if (data.length === 1000) return pullTable(table as never, tsCol as never);
}

async function pullTimersFresh() {
  // Open timers are few; pull all open + last day of closed ones.
  const { data, error } = await supabase
    .from('timers')
    .select('*')
    .or(
      `and(completed_at.is.null,canceled_at.is.null),created_at.gt.${new Date(Date.now() - 86400000).toISOString()}`
    );
  if (!error && data) {
    await db.timers.clear();
    await db.timers.bulkPut(data);
  }
}

async function pullFeedLogWindow() {
  const since = new Date(Date.now() - LOG_WINDOW_DAYS * 86400000).toISOString();
  const { data, error } = await supabase
    .from('feed_logs')
    .select('*')
    .gt('fed_at', since)
    .order('fed_at', { ascending: false })
    .limit(4000);
  if (error || !data) return;

  // Rows with pending outbox ops are locally authoritative: a queued delete
  // must not be resurrected by the pull, and a queued insert must not be
  // wiped before it replays.
  const pending = await db.outbox.filter((op) => op.table === 'feed_logs').toArray();
  const pendingDeletes = new Set(
    pending.filter((op) => op.op === 'delete').map((op) => op.match?.id as string)
  );
  const pendingInserts = new Set(
    pending.filter((op) => op.op === 'insert').map((op) => (op.values as { id?: string })?.id)
  );

  await db.transaction('rw', db.feed_logs, async () => {
    const stale = await db.feed_logs.where('fed_at').above(since).primaryKeys();
    await db.feed_logs.bulkDelete(stale.filter((id) => !pendingInserts.has(id as string)));
    await db.feed_logs.bulkPut(data.filter((r) => !pendingDeletes.has(r.id)));
  });
}

async function pullProfile() {
  const { data } = await supabase.from('profile').select('*').maybeSingle();
  if (data) await setCachedProfile(data);
}

/** Hard deletes don't flow through watermarks — reconcile by ID sweep.
 *  Cheap even at thousands of rows (ids only). */
async function reconcileDeletes() {
  const [{ data: aIds }, { data: sIds }] = await Promise.all([
    supabase.from('animals').select('id').limit(20000),
    supabase.from('schedules').select('id').limit(20000),
  ]);
  if (aIds) {
    const live = new Set(aIds.map((r) => r.id));
    const stale = (await db.animals.toCollection().primaryKeys()).filter((id) => !live.has(id));
    if (stale.length) await db.animals.bulkDelete(stale);
  }
  if (sIds) {
    const live = new Set(sIds.map((r) => r.id));
    const stale = (await db.schedules.toCollection().primaryKeys()).filter((id) => !live.has(id));
    if (stale.length) await db.schedules.bulkDelete(stale);
  }
}

export async function syncNow(): Promise<void> {
  if (syncing.value || !navigator.onLine) return;
  syncing.value = true;
  try {
    await Promise.all([
      pullTable('animals', 'updated_at'),
      pullTable('schedules', 'updated_at'),
      pullTable('documents', 'updated_at'),
      pullTable('photos', 'created_at'),
      pullTable('reports', 'created_at'),
      pullTable('messages', 'created_at'),
      pullFeedLogWindow(),
      pullTimersFresh(),
      pullProfile(),
      reconcileDeletes(),
    ]);
    lastSyncAt.value = new Date();
    await reloadFromCache();
  } catch (e) {
    console.warn('sync failed', e);
  } finally {
    syncing.value = false;
  }
}

export function initSync() {
  void syncNow();
  window.addEventListener('online', () => void syncNow());
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) void syncNow();
  });
  setInterval(() => void syncNow(), 60_000);
}
